// "Leitura da IA" da campanha de recuperação. Mesma linguagem visual do
// HeatmapAiInsight (Painel 360). Contrato: GET /customer-recovery-rules/:id/ai-insight.
import React, { useEffect, useState } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { useLocaleState } from 'react-admin';
import { DEFAULT_API_ADMIN } from '../../../constants';
import { httpClientReactAdmin } from '../../../lib';
import { COLORS, shimmer } from '../../dashboards/shared';

const MAX_RETRIES = 4;
const DEFAULT_RETRY_SECONDS = 6;
const AI_GRADIENT = 'linear-gradient(135deg, #6366f1, #a855f7)';

type Verdict = 'early' | 'good' | 'attention' | 'bad';
type PointKind = 'positive' | 'warning' | 'idea';
interface Insight { verdict: Verdict; headline: string; summary: string | null; points: Array<{ kind: PointKind; text: string }>; }
interface InsightResponse { status: 'ready' | 'generating' | 'unavailable'; insight: Insight | null; retryAfterSeconds: number | null; }

const VERDICT: Record<Verdict, { label: string; color: 'info' | 'success' | 'warning' | 'error' }> = {
  early: { label: 'Começo da campanha', color: 'info' },
  good: { label: 'Indo bem', color: 'success' },
  attention: { label: 'Pede atenção', color: 'warning' },
  bad: { label: 'Não está funcionando', color: 'error' },
};

const POINT_ICON: Record<PointKind, { Icon: React.ElementType; color: string }> = {
  positive: { Icon: CheckCircleOutlineIcon, color: COLORS.success },
  warning: { Icon: WarningAmberIcon, color: COLORS.warning },
  idea: { Icon: LightbulbOutlinedIcon, color: '#7c3aed' },
};

const containerSx = {
  mt: 2, maxWidth: 920, display: 'flex', flexDirection: 'column' as const, gap: 1,
  borderRadius: 3, p: 1.75,
  background: 'linear-gradient(135deg, rgba(99,102,241,0.07), rgba(168,85,247,0.08))',
  border: '1px solid rgba(139,92,246,0.22)',
};

const Badge = () => (
  <Box sx={{ width: 22, height: 22, borderRadius: 1.5, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: AI_GRADIENT }}>
    <AutoAwesomeIcon sx={{ fontSize: 13, color: '#fff' }} />
  </Box>
);

const Skeleton = () => (
  <Box sx={containerSx}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Badge />
      <Typography sx={{
        fontSize: 12.5, fontWeight: 600,
        background: 'linear-gradient(90deg, #a5b4fc 25%, #d8b4fe 50%, #a5b4fc 75%)',
        backgroundSize: '200% 100%', animation: `${shimmer} 1.4s ease-in-out infinite`,
        WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent',
      }}>
        A IA está lendo a campanha…
      </Typography>
    </Box>
  </Box>
);

const CampaignAiInsight: React.FC<{ campaignId: string }> = ({ campaignId }) => {
  const [localeState] = useLocaleState();
  const locale = localeState === 'es' ? 'es' : 'pt-br';
  const [state, setState] = useState<{ loading: boolean; data: InsightResponse | null }>({ loading: true, data: null });

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    setState({ loading: true, data: null });
    const load = async (attempt: number) => {
      try {
        const { json } = await httpClientReactAdmin(`${DEFAULT_API_ADMIN}/customer-recovery-rules/${campaignId}/ai-insight?locale=${locale}`);
        if (cancelled) return;
        const data: InsightResponse | undefined = json?.data;
        if (data?.status === 'generating' && attempt < MAX_RETRIES) {
          timer = setTimeout(() => load(attempt + 1), (data.retryAfterSeconds ?? DEFAULT_RETRY_SECONDS) * 1000);
          return;
        }
        setState({ loading: false, data: data?.status === 'ready' ? data : null });
      } catch {
        if (!cancelled) setState({ loading: false, data: null });
      }
    };
    load(0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [campaignId, locale]);

  if (state.loading) return <Skeleton />;
  const insight = state.data?.insight;
  if (!insight) return null;
  const verdict = VERDICT[insight.verdict];
  const points = (insight.points ?? []).slice(0, 4);

  return (
    <Box sx={containerSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Badge />
        <Typography sx={{ fontSize: 12.5, fontWeight: 800, background: AI_GRADIENT, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          Leitura da IA
        </Typography>
        {verdict && <Chip size="small" label={verdict.label} color={verdict.color} />}
      </Box>
      {/* Texto da IA sempre como texto puro, nunca dangerouslySetInnerHTML. */}
      <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: COLORS.ink }}>{insight.headline}</Typography>
      {insight.summary && <Typography sx={{ fontSize: 12.5, color: '#334155' }}>{insight.summary}</Typography>}
      {points.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {points.map((p, i) => {
            const { Icon, color } = POINT_ICON[p.kind] ?? POINT_ICON.idea;
            return (
              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                <Icon sx={{ fontSize: 15, color, mt: 0.2, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 12.5, color: '#334155' }}>{p.text}</Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default CampaignAiInsight;
